import React, { useMemo, useState } from "react";
import { ChevronDown, ChevronUp, Clock, PhoneCall } from "lucide-react";

const fmt = (iso) =>
  new Date(iso).toLocaleString("it-IT", { weekday: "short", day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });

const bucket = (iso) => {
  if (!iso) return { key: "none", label: "Senza data", cls: "border-neutral-400 text-neutral-600 bg-neutral-100" };
  const d = new Date(iso);
  const now = new Date();
  if (d < now) return { key: "late", label: "In ritardo", cls: "border-red-600 text-white bg-red-600" };
  if (d.toDateString() === now.toDateString()) return { key: "today", label: "Oggi", cls: "border-[#FF5A1F] text-white bg-[#FF5A1F]" };
  return { key: "next", label: "Prossimo", cls: "border-violet-600 text-violet-700 bg-violet-50" };
};

export const RecallsPanel = ({ items, onOpenDetail, onPlanRecall }) => {
  const [open, setOpen] = useState(true);
  const rows = useMemo(
    () =>
      (items || [])
        .filter((l) => l.setting_status === "richiama_dopo")
        .sort((a, b) => {
          if (!a.setting_richiama_at) return 1;
          if (!b.setting_richiama_at) return -1;
          return new Date(a.setting_richiama_at) - new Date(b.setting_richiama_at);
        }),
    [items],
  );
  const late = rows.filter((r) => bucket(r.setting_richiama_at).key === "late").length;
  const today = rows.filter((r) => bucket(r.setting_richiama_at).key === "today").length;

  return (
    <section className="mb-8 border-2 border-violet-600 bg-white" data-testid="recalls-panel">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-violet-50 transition-colors"
        data-testid="recalls-toggle"
      >
        <span className="flex items-center gap-2 font-display text-xl uppercase">
          <Clock size={18} className="text-violet-600" /> Richiama dopo · {rows.length}
        </span>
        <span className="flex items-center gap-3 text-xs font-bold">
          {late > 0 && <span className="text-red-600" data-testid="recalls-late-count">{late} in ritardo</span>}
          {today > 0 && <span className="text-[#FF5A1F]" data-testid="recalls-today-count">{today} oggi</span>}
          {open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </span>
      </button>
      {open && (
        <div className="px-4 pb-4">
          {rows.length === 0 ? (
            <div className="text-sm text-[#0A0A0A]/60" data-testid="recalls-empty">Nessun lead da richiamare.</div>
          ) : (
            rows.map((l) => {
              const b = bucket(l.setting_richiama_at);
              const lastNote = (l.notes || []).filter((n) => n.text).slice(-1)[0];
              return (
                <div key={l.id} className="flex flex-wrap items-center gap-3 border-t border-[#0A0A0A]/10 py-2 text-xs" data-testid={`recall-row-${l.id}`}>
                  <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 border-2 min-w-[90px] text-center ${b.cls}`}>{b.label}</span>
                  <span className="font-bold w-[190px]" data-testid={`recall-when-${l.id}`}>
                    {l.setting_richiama_at ? fmt(l.setting_richiama_at) : "—"}
                  </span>
                  <button
                    onClick={() => onOpenDetail(l)}
                    className="font-display text-base uppercase hover:text-[#FF5A1F] text-left"
                    data-testid={`recall-name-${l.id}`}
                  >
                    {l.artist_name || "—"}
                  </button>
                  {l.telefono && (
                    <a href={`tel:${l.telefono}`} className="flex items-center gap-1 text-[#0A0A0A]/70 hover:text-[#FF5A1F]">
                      <PhoneCall size={11} /> {l.telefono}
                    </a>
                  )}
                  {lastNote && <span className="text-[#0A0A0A]/50 truncate max-w-[320px]" title={lastNote.text}>📝 {lastNote.text}</span>}
                  <button
                    onClick={() => onPlanRecall(l)}
                    className="ml-auto text-[10px] uppercase tracking-widest font-bold px-2 py-1 border-2 border-violet-600 text-violet-700 hover:bg-violet-50 transition-colors"
                    data-testid={`recall-replan-${l.id}`}
                  >
                    {l.setting_richiama_at ? "Sposta" : "Metti data e ora"}
                  </button>
                </div>
              );
            })
          )}
        </div>
      )}
    </section>
  );
};
